import {
  REGISTRANT_QUESTION_COLUMN_PREFIX,
  getRegistrantLicenseColumnDisplayName,
} from "@/components/registrant/dashboard/license-list/license-table";
import type { RegistrantLicenseColumnKey } from "@/components/registrant/dashboard/license-list/license-table";
import {
  EMPTY_FILTER_VALUE,
  NOT_BLANK_FILTER_VALUE,
  decodeContainsFilterValue,
  displayValueMatchesFilters,
  presenceFilterLabel,
} from "./dashboard-column-filter-match";
import type { ColumnProperties } from "./dashboard-table";
import {
  defaultDateFilterValue,
  formatDateRangeFilterLabel,
  matchesDateRangeFilter,
  parseDateRangeFilterValue,
  resolveColumnFilterType,
} from "./dashboard-table-date-filter";

export type ColumnFilterBadge = {
  key: string;
  label: string;
  columnId: string;
  value: string;
  /** Date-range chips represent the whole column; removing one clears it. */
  clearsColumn?: boolean;
};

function normalizeCellFilterValue(
  cellValue: string | null | undefined,
): string {
  const value = cellValue ?? "";
  return !value || value.trim() === "" ? EMPTY_FILTER_VALUE : value;
}

function activeColumnFilterEntries(
  columnFilters: Record<string, string[]> | undefined,
): Array<[string, string[]]> {
  if (!columnFilters) return [];
  return Object.entries(columnFilters).filter(
    ([, values]) => Array.isArray(values) && values.length > 0,
  );
}

export function rowMatchesColumnFilters<T extends object, K extends string>(
  row: T,
  columnFilters: Record<string, string[]> | undefined,
  accessorKeys: readonly K[],
  getColumnProperties: (accessorKey: K) => ColumnProperties<T, K>,
): boolean {
  const activeFilters = activeColumnFilterEntries(columnFilters);
  if (activeFilters.length === 0) return true;

  for (const [columnId, values] of activeFilters) {
    if (!accessorKeys.includes(columnId as K)) continue;

    const accessorKey = columnId as K;
    const columnProps = getColumnProperties(accessorKey);
    const filterType = resolveColumnFilterType(
      columnId,
      columnProps.filterType,
    );

    if (filterType === "dateRange") {
      const rawValue = columnProps.getFilterValue
        ? columnProps.getFilterValue(row, accessorKey)
        : defaultDateFilterValue(row, accessorKey, columnProps.getValue);
      if (
        !matchesDateRangeFilter(rawValue, parseDateRangeFilterValue(values))
      ) {
        return false;
      }
      continue;
    }

    const displayValue = normalizeCellFilterValue(
      columnProps.getValue(row, accessorKey),
    );
    if (columnId === "regions") {
      const isBlank =
        displayValue === EMPTY_FILTER_VALUE || displayValue === "Unassigned";
      const names = isBlank
        ? ["Unassigned"]
        : displayValue.split(",").map((name) => name.trim());
      const matches = values.some((value) => {
        if (value === NOT_BLANK_FILTER_VALUE) return !isBlank;
        if (value === EMPTY_FILTER_VALUE || value === "blank") return isBlank;
        const bare = value.replace(/ \([^)]*\)$/, "");
        return names.some(
          (name) =>
            name.localeCompare(value, undefined, { sensitivity: "accent" }) ===
              0 ||
            name.localeCompare(bare, undefined, { sensitivity: "accent" }) ===
              0,
        );
      });
      if (!matches) return false;
      continue;
    }
    if (!displayValueMatchesFilters(displayValue, values)) {
      return false;
    }
  }

  return true;
}

export function filterRowsByColumnFilters<T extends object, K extends string>(
  data: T[],
  columnFilters: Record<string, string[]> | undefined,
  accessorKeys: readonly K[],
  getColumnProperties: (accessorKey: K) => ColumnProperties<T, K>,
): T[] {
  const activeFilters = activeColumnFilterEntries(columnFilters);
  if (activeFilters.length === 0) return data;

  return data.filter((row) =>
    rowMatchesColumnFilters(
      row,
      columnFilters,
      accessorKeys,
      getColumnProperties,
    ),
  );
}

export function buildColumnFilterBadges<T extends object, K extends string>(
  columnFilters: Record<string, string[]> | undefined,
  accessorKeys: readonly K[],
  getColumnProperties: (accessorKey: K) => ColumnProperties<T, K>,
): ColumnFilterBadge[] {
  const badges: ColumnFilterBadge[] = [];

  for (const [columnId, values] of activeColumnFilterEntries(columnFilters)) {
    if (!accessorKeys.includes(columnId as K)) continue;

    const columnProps = getColumnProperties(columnId as K);
    const displayName = columnProps.displayName;
    const filterType = resolveColumnFilterType(
      columnId,
      columnProps.filterType,
    );

    if (filterType === "dateRange") {
      const bounds = parseDateRangeFilterValue(values);
      const label = formatDateRangeFilterLabel(bounds);
      if (!label) continue;
      badges.push({
        key: `${columnId}:${label}`,
        label: `${displayName}: ${label}`,
        columnId,
        value: label,
        clearsColumn: true,
      });
      continue;
    }

    for (const value of values) {
      badges.push({
        key: `${columnId}:${value}`,
        label: columnFilterValueBadgeLabel(displayName, value),
        columnId,
        value,
      });
    }
  }

  return badges;
}

function columnFilterValueBadgeLabel(
  displayName: string,
  value: string,
): string {
  const presence = presenceFilterLabel(value);
  if (presence) return `${displayName}: ${presence}`;
  const containsValue = decodeContainsFilterValue(value);
  if (containsValue !== null) {
    return `${displayName} contains “${containsValue}”`;
  }
  return `${displayName}: ${value}`;
}

export function buildRegistrantColumnFilterBadges(
  columnFilters: Record<string, string[]> | undefined,
): ColumnFilterBadge[] {
  const badges: ColumnFilterBadge[] = [];

  for (const [columnId, values] of activeColumnFilterEntries(columnFilters)) {
    // Registrant question columns are keyed by their own label.
    const displayName = columnId.startsWith(REGISTRANT_QUESTION_COLUMN_PREFIX)
      ? columnId.slice(REGISTRANT_QUESTION_COLUMN_PREFIX.length)
      : getRegistrantLicenseColumnDisplayName(
          columnId as RegistrantLicenseColumnKey,
        );

    for (const value of values) {
      badges.push({
        key: `${columnId}:${value}`,
        label: columnFilterValueBadgeLabel(displayName, value),
        columnId,
        value,
      });
    }
  }

  return badges;
}

export function removeColumnFilterBadge(
  columnFilters: Record<string, string[]> | undefined,
  badge: Pick<ColumnFilterBadge, "columnId" | "value" | "clearsColumn">,
): Record<string, string[]> {
  const next = { ...(columnFilters ?? {}) };
  if (badge.clearsColumn) {
    delete next[badge.columnId];
    return next;
  }
  const remaining = (next[badge.columnId] ?? []).filter(
    (item) => item !== badge.value,
  );
  if (remaining.length > 0) {
    next[badge.columnId] = remaining;
  } else {
    delete next[badge.columnId];
  }
  return next;
}
